import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, apiError, eur } from "@/pages/mrv/mrvApi";
import MRVLayout from "@/pages/mrv/MRVLayout";
import OutcomeBadge from "@/pages/rc/OutcomeBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Calculator, Save, HeartHandshake, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "—");

export default function MRVNewQuote() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgQuery, setOrgQuery] = useState("");
  const [orgOpen, setOrgOpen] = useState(false);

  const [orgId, setOrgId] = useState(searchParams.get("ente") || "");
  const [orgName, setOrgName] = useState(searchParams.get("nome") || "");
  const [opzioneTipo, setOpzioneTipo] = useState("A");
  const [combinazione, setCombinazione] = useState("");
  const [nVolontari, setNVolontari] = useState("");
  const [dataEffetto, setDataEffetto] = useState(new Date().toISOString().slice(0, 10));

  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  // load config + orgs
  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
  }, []);

  // load existing quote (edit)
  useEffect(() => {
    if (!id) return;
    api.get(`/quotes/${id}`).then((r) => {
      const inp = r.data.input_payload || {};
      setOrgId(r.data.organization_id || "");
      setOrgName(r.data.organization?.ragione_sociale || "");
      setOpzioneTipo((inp.opzione_tipo || "A").toUpperCase());
      setCombinazione(inp.combinazione || "");
      setNVolontari(String(inp.n_volontari ?? ""));
      if (inp.data_effetto) setDataEffetto(inp.data_effetto);
      setResult(r.data.premium_result ? { status: r.data.calc_status, premium: r.data.premium_result, eligibility: r.data.eligibility_result } : null);
    }).catch(() => {});
  }, [id]);

  // resolve org name from prefilled id
  useEffect(() => {
    if (orgId && !orgName && orgs.length) {
      const o = orgs.find((x) => x.id === orgId);
      if (o) setOrgName(o.ragione_sociale);
    }
  }, [orgId, orgName, orgs]);

  const filteredOrgs = useMemo(() => {
    const q = orgQuery.trim().toLowerCase();
    if (!q) return orgs.slice(0, 30);
    return orgs.filter((o) =>
      (o.ragione_sociale || "").toLowerCase().includes(q) ||
      (o.partita_iva || "").toLowerCase().includes(q) ||
      (o.codice_fiscale || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [orgQuery, orgs]);

  const selectedCombo = config?.combinazioni?.find((c) => c.code === combinazione);

  const buildPayload = () => ({
    organization_id: orgId || null,
    opzione_tipo: opzioneTipo,
    combinazione,
    n_volontari: parseInt(nVolontari, 10) || 0,
    data_effetto: dataEffetto,
  });

  const canCalc = combinazione && parseInt(nVolontari, 10) > 0;

  const doCalculate = async () => {
    if (!canCalc) { toast.error("Seleziona combinazione e inserisci il numero di volontari."); return; }
    setCalculating(true);
    try {
      const { data } = await api.post("/quotes/calculate", buildPayload());
      setResult(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setCalculating(false); }
  };

  const doSave = async () => {
    if (!canCalc) { toast.error("Configura il preventivo prima di salvare."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/quotes", { ...buildPayload(), quote_id: id || null });
      toast.success("Preventivo salvato.");
      navigate(`/multirischi-volontariato/preventivi/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const prem = result?.premium;

  return (
    <MRVLayout>
      <div data-testid="mrv-new-quote-page">
        <div className="flex items-center gap-2 mb-1">
          <HeartHandshake className="w-5 h-5 text-teal-600" />
          <h1 className="font-display text-3xl font-semibold tracking-tight text-stone-900">
            {id ? "Modifica Preventivo" : "Nuovo Preventivo"}
          </h1>
        </div>
        <p className="text-stone-500 text-sm mb-6">Tutela Multirischi del Volontariato · min. {config?.min_volontari ?? 4} volontari · premio minimo € {config?.min_premio_polizza ?? 200}.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Ente */}
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-5">
              <h2 className="font-display text-lg font-medium mb-4">Ente / Contraente</h2>
              <div className="relative">
                <div className="flex items-center gap-2 border border-stone-200 rounded-sm px-3 py-2 bg-stone-50">
                  <Building2 className="w-4 h-4 text-stone-400" />
                  <span className="text-sm text-stone-700 flex-1 truncate" data-testid="mrv-selected-org">
                    {orgName || "Nessun ente selezionato"}
                  </span>
                  <button data-testid="mrv-change-org" onClick={() => setOrgOpen((v) => !v)}
                    className="text-xs text-teal-700 hover:underline">
                    {orgName ? "Cambia" : "Seleziona"}
                  </button>
                </div>
                {orgOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-stone-200 rounded-sm shadow-lg">
                    <Input autoFocus data-testid="mrv-org-search" placeholder="Cerca ente…"
                      value={orgQuery} onChange={(e) => setOrgQuery(e.target.value)} className="m-2 w-[calc(100%-1rem)] rounded-sm" />
                    <div className="max-h-56 overflow-y-auto">
                      {filteredOrgs.map((o) => (
                        <button key={o.id} data-testid={`mrv-org-option-${o.id}`}
                          onClick={() => { setOrgId(o.id); setOrgName(o.ragione_sociale); setOrgOpen(false); setOrgQuery(""); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 border-b border-stone-50">
                          <div className="text-stone-800">{o.ragione_sociale}</div>
                          <div className="text-xs text-stone-400 font-mono">{o.partita_iva || o.codice_fiscale || ""}</div>
                        </button>
                      ))}
                      {filteredOrgs.length === 0 && <div className="px-3 py-3 text-xs text-stone-400">Nessun ente trovato.</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Copertura */}
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-5 space-y-5">
              <h2 className="font-display text-lg font-medium">Configurazione copertura</h2>

              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Opzione</Label>
                <div className="grid grid-cols-2 gap-3">
                  {(config?.opzioni_tipo || []).map((o) => (
                    <button key={o.code} data-testid={`mrv-opzione-${o.code}`}
                      onClick={() => setOpzioneTipo(o.code)}
                      className={`text-left px-4 py-3 rounded-sm border transition-all ${opzioneTipo === o.code ? "border-teal-600 bg-teal-50" : "border-stone-200 hover:border-stone-300"}`}>
                      <div className={`font-semibold text-sm ${opzioneTipo === o.code ? "text-teal-800" : "text-stone-800"}`}>Opzione {o.code}</div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {o.code === "A" ? "RC + Infortuni + Malattia" : "solo Infortuni + Malattia"}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 mb-1.5 block">Combinazione di garanzie</Label>
                <Select value={combinazione} onValueChange={setCombinazione}>
                  <SelectTrigger data-testid="mrv-combinazione" className="rounded-sm"><SelectValue placeholder="Seleziona combinazione…" /></SelectTrigger>
                  <SelectContent>
                    {(config?.combinazioni || []).map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCombo && opzioneTipo === "A" && (
                  <p className="text-xs text-stone-500 mt-2" data-testid="mrv-rc-info">
                    RC verso terzi massimale € {selectedCombo.rc_massimale.toLocaleString("it-IT")} · € {selectedCombo.rc_unit}/testa (lordo)
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 mb-1.5 block">Numero di volontari</Label>
                <Input data-testid="mrv-n-volontari" type="number" min={config?.min_volontari ?? 4}
                  value={nVolontari} onChange={(e) => setNVolontari(e.target.value)}
                  placeholder={`min. ${config?.min_volontari ?? 4}`} className="rounded-sm max-w-[200px]" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 mb-1.5 block">Data di effetto</Label>
                <Input data-testid="mrv-data-effetto" type="date" value={dataEffetto}
                  onChange={(e) => setDataEffetto(e.target.value)} className="rounded-sm max-w-[200px]" />
                <p className="text-xs text-stone-400 mt-1">Rateo corto fino al 31/12; scadenza annuale al 31/12 dell'anno successivo.</p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button data-testid="mrv-calculate-btn" onClick={doCalculate} disabled={!canCalc || calculating} className="rounded-sm bg-stone-900 hover:bg-stone-800">
                  <Calculator className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {calculating ? "Calcolo…" : "Calcola premio"}
                </Button>
                <Button data-testid="mrv-save-btn" onClick={doSave} disabled={!canCalc || saving} variant="outline" className="rounded-sm">
                  <Save className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {saving ? "Salvataggio…" : "Salva preventivo"}
                </Button>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-5 sticky top-8" data-testid="mrv-preview">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-medium">Anteprima premio</h2>
                {result && <OutcomeBadge status={result.status} />}
              </div>

              {!result && <p className="text-sm text-stone-400">Calcola il premio per vedere l'anteprima.</p>}

              {result?.eligibility?.blocking_errors?.map((e, i) => (
                <p key={i} className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2 mb-2 flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /> {e}
                </p>
              ))}

              {prem && (
                <>
                  <table className="w-full text-sm mb-3">
                    <tbody className="font-mono">
                      {Object.entries(prem.breakdown).map(([k, v]) => (
                        <tr key={k} className="border-b border-stone-100">
                          <td className="py-2 font-sans text-stone-600 text-xs pr-2">{config?.garanzia_labels?.[k] || k}</td>
                          <td className="py-2 text-right">{eur(v.gross)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-between border-t border-stone-200 pt-3">
                    <span className="text-xs uppercase tracking-widest text-stone-500">Premio annuo da tariffa</span>
                    <span className="font-display text-xl font-semibold text-stone-900" data-testid="mrv-preview-tariff">{eur(prem.tariff_gross_total)}</span>
                  </div>
                  {prem.minimum_premium_applicable && (
                    <>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-xs uppercase tracking-widest text-amber-700">Premio minimo di polizza</span>
                        <span className="font-mono text-amber-800" data-testid="mrv-preview-minimo">{eur(prem.minimum_premium_value)}</span>
                      </div>
                      <div data-testid="mrv-min-premium-badge" className="mt-2 px-3 py-2 bg-amber-100 border border-amber-300 rounded-sm text-xs text-amber-900">
                        Il premio da tariffa è inferiore al minimo di polizza. In fase di dettaglio/stampa potrai scegliere quale premio consegnare.
                      </div>
                    </>
                  )}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-200">
                    <span className="text-xs uppercase tracking-widest text-teal-700">Premio applicato</span>
                    <span className="font-display text-2xl font-semibold text-teal-800" data-testid="mrv-preview-total">{eur(prem.gross_total)}</span>
                  </div>
                  {prem.rateo && (
                    <div className="mt-3 pt-3 border-t border-stone-200 text-sm space-y-1" data-testid="mrv-preview-rateo">
                      <div className="flex items-center justify-between">
                        <span className="text-xs uppercase tracking-widest text-stone-500">Rateo ({prem.rateo.giorni} gg / 365)</span>
                        <span className="font-mono text-stone-800">{eur(prem.rateo.importo)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-stone-400">
                        <span>Scadenza annuale</span>
                        <span className="font-mono">{fmtDate(prem.rateo.scadenza_annuale)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </MRVLayout>
  );
}
